import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';

import type { Listing } from 'app/types/listing';
import { ListingType } from 'app/types/listing';

export type ListingAvailabilityPresentation =
  | {
      kind: 'available';
      labelKey: 'availability.availableNow';
      nextLoanStart?: Date;
    }
  | {
      kind: 'borrowed';
      labelKey: 'availability.borrowedUntil';
      date: Date;
      availableInDays?: number;
    }
  | {
      kind: 'reserved';
      labelKey: 'availability.availableUntil';
      date: Date;
    }
  | {
      kind: 'unavailable';
      labelKey: 'availability.currentlyUnavailable' | 'availability.currentlyBorrowed';
    }
  | {
      kind: 'request';
      labelKey: 'availability.flexibleDates' | 'availability.requestDate' | 'availability.requestDateRange';
      start?: Date;
      end?: Date;
    };

function isValidDate(date: Date) {
  return !Number.isNaN(date.getTime());
}

export function getListingAvailability(
  listing: Pick<Listing, 'end' | 'isCurrentlyUnavailable' | 'loans' | 'start' | 'status' | 'type'>,
  now = new Date(),
): ListingAvailabilityPresentation {
  if (listing.type === ListingType.request) {
    const start = listing.start ? new Date(listing.start) : undefined;
    const end = listing.end ? new Date(listing.end) : undefined;
    const validStart = start && isValidDate(start) ? start : undefined;
    const validEnd = end && isValidDate(end) ? end : undefined;

    if (!validStart) {
      return { kind: 'request', labelKey: 'availability.flexibleDates' };
    }

    if (!validEnd || validStart.getTime() === validEnd.getTime()) {
      return { kind: 'request', labelKey: 'availability.requestDate', start: validStart };
    }

    return {
      kind: 'request',
      labelKey: 'availability.requestDateRange',
      start: validStart,
      end: validEnd,
    };
  }

  const loans = (listing.loans ?? [])
    .flatMap((loan) => {
      const datedLoan = {
        ...loan,
        startDate: new Date(loan.start),
        endDate: new Date(loan.end),
      };
      return isValidDate(datedLoan.startDate) && isValidDate(datedLoan.endDate) ? [datedLoan] : [];
    })
    .sort((left, right) => left.startDate.getTime() - right.startDate.getTime());

  const today = startOfDay(now);
  const activeLoan = loans.find((loan) => startOfDay(loan.startDate) <= today && startOfDay(loan.endDate) >= today);

  if (activeLoan) {
    let chainEnd = activeLoan.endDate;
    for (const loan of loans) {
      const loanStart = startOfDay(loan.startDate);
      const loanEnd = startOfDay(loan.endDate);
      if (loanEnd < today) continue;
      const chainEndDay = startOfDay(chainEnd);
      if (loanStart.getTime() <= addDays(chainEndDay, 1).getTime()) {
        if (loanEnd.getTime() > chainEndDay.getTime()) chainEnd = loan.endDate;
      } else {
        break;
      }
    }
    const borrowedUntil = chainEnd;
    const borrowedUntilDay = Date.UTC(
      borrowedUntil.getUTCFullYear(),
      borrowedUntil.getUTCMonth(),
      borrowedUntil.getUTCDate(),
    );
    const todayDay = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());

    return {
      kind: 'borrowed',
      labelKey: 'availability.borrowedUntil',
      date: borrowedUntil,
      availableInDays: Math.max(1, differenceInCalendarDays(borrowedUntilDay, todayDay) + 1),
    };
  }

  if (listing.isCurrentlyUnavailable || listing.status === 'unavailable') {
    return { kind: 'unavailable', labelKey: 'availability.currentlyUnavailable' };
  }

  if (listing.status === 'loaned') {
    return { kind: 'unavailable', labelKey: 'availability.currentlyBorrowed' };
  }

  const nextLoan = loans.find((loan) => loan.startDate.getTime() > now.getTime());
  if (nextLoan) {
    return {
      kind: 'reserved',
      labelKey: 'availability.availableUntil',
      date: nextLoan.startDate,
    };
  }

  return { kind: 'available', labelKey: 'availability.availableNow' };
}
